<template>
  <v-card>
    <v-card-title class="pb-2">
      Tickets
      <v-spacer />
      <v-btn small icon @click="reload" :loading="loading_done" title="DONE/CANCELLED nachladen">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-0 pb-2">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Suchen…"
        outlined
        dense
        hide-details
        clearable
        single-line
        class="mb-2"
      />

      <v-chip-group
        v-model="scope"
        column
        mandatory
        active-class="primary white--text"
        class="ticket-chip-group"
      >
        <v-chip
          v-for="opt in scope_options"
          :key="opt.value"
          :value="opt.value"
          small
          label
        >
          <v-icon x-small left>{{ opt.icon }}</v-icon>
          {{ opt.label }}
          <span v-if="counts[opt.value] != null" class="ml-1 text-caption">
            ({{ counts[opt.value] }})
          </span>
        </v-chip>
      </v-chip-group>

      <v-chip-group
        v-model="status"
        column
        mandatory
        active-class="primary--text"
        class="ticket-chip-group mt-1"
      >
        <v-chip
          v-for="opt in status_options"
          :key="opt.value"
          :value="opt.value"
          small
          outlined
        >
          <v-icon x-small left>{{ opt.icon }}</v-icon>
          {{ opt.label }}
        </v-chip>
      </v-chip-group>
    </v-card-text>

    <v-divider />

    <v-list two-line class="ticket-list pt-0">
      <template v-if="filtered.length === 0">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle class="grey--text">
              Keine Tickets in dieser Auswahl.
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-else>
        <v-list-item
          v-for="item in filtered"
          :key="item.id"
          :input-value="value && value.id === item.id"
          active-class="primary--text"
          @click="select(item)"
        >
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              <span class="text-truncate">{{ item.title }}</span>
              <v-icon
                v-if="item.type === 'STATION_REFILL'"
                small
                class="ml-1"
                title="Tauschkiste"
              >mdi-swap-horizontal</v-icon>
            </v-list-item-title>

            <v-list-item-subtitle class="text--primary text-caption">
              <v-icon x-small class="mr-1">
                {{ assigneeIcon(item) }}
              </v-icon>
              {{ assigneeLabel(item) }}
            </v-list-item-subtitle>

            <v-list-item-subtitle class="text-caption">
              {{ currentNodeLabel(item) }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-list-item-action-text class="text-caption">
              {{ formatDate(item.updated_at) }}
            </v-list-item-action-text>
            <v-chip
              x-small
              :color="state_config(item).color"
              :text-color="state_config(item).text || 'white'"
            >
              {{ state_config(item).name }}
            </v-chip>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-list>
  </v-card>
</template>

<script>
const STATE_CONFIG = {
  OPEN:      { name: "Offen",      color: "success" },
  DONE:      { name: "Erledigt",   color: "grey" },
  CANCELLED: { name: "Abgebrochen", color: "grey lighten-1", text: "grey darken-2" },
}

export default {
  name: "TicketList",
  props: {
    tickets: { type: Array, default: () => [] },
    value: { type: Object, default: null },
  },
  data: () => ({
    search: "",
    scope: "all",
    status: "OPEN",
    loading_done: false,
    scope_options: [
      { value: "all",      label: "Alle",          icon: "mdi-format-list-bulleted" },
      { value: "mine",     label: "Meine",         icon: "mdi-account-edit" },
      { value: "assigned", label: "Mir zugewiesen", icon: "mdi-account-arrow-right" },
      { value: "group",    label: "Meiner Gruppe", icon: "mdi-account-group" },
    ],
    status_options: [
      { value: "OPEN",      label: "Offen",       icon: "mdi-progress-clock" },
      { value: "DONE",      label: "Erledigt",    icon: "mdi-check" },
      { value: "CANCELLED", label: "Abgebrochen", icon: "mdi-cancel" },
      { value: "all",       label: "Alle",        icon: "mdi-asterisk" },
    ],
  }),
  computed: {
    me() {
      return this.$store.state.user
    },
    my_groups() {
      return this.$store.state.user.pms || []
    },
    after_scope() {
      const list = this.tickets || []
      switch (this.scope) {
        case "mine":     return list.filter(t => this.is_mine(t))
        case "assigned": return list.filter(t => this.is_assigned_to_me(t))
        case "group":    return list.filter(t => this.is_assigned_to_my_group(t))
        default:         return list
      }
    },
    filtered() {
      const q = (this.search || "").trim().toLowerCase()
      let list = this.after_scope
      if (this.status !== "all") {
        list = list.filter(t => t.state === this.status)
      }
      if (q) {
        list = list.filter(t =>
          (t.title || "").toLowerCase().includes(q) ||
          (t.text || "").toLowerCase().includes(q)
        )
      }
      return list.slice().sort((a, b) =>
        new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
      )
    },
    counts() {
      const list = this.tickets || []
      const open = (t) => this.status === "all" || t.state === this.status
      return {
        all:      list.filter(open).length,
        mine:     list.filter(t => open(t) && this.is_mine(t)).length,
        assigned: list.filter(t => open(t) && this.is_assigned_to_me(t)).length,
        group:    list.filter(t => open(t) && this.is_assigned_to_my_group(t)).length,
      }
    },
  },
  methods: {
    select(item) {
      const same = this.value && this.value.id === item.id
      this.$emit("input", same ? null : item)
    },
    reload() {
      this.loading_done = true
      Promise.all([
        this.$store.dispatch("update", { context: "/tickets?filter_state=DONE" }),
        this.$store.dispatch("update", { context: "/tickets?filter_state=CANCELLED" }),
      ]).finally(() => { this.loading_done = false })
    },
    current_node_index(t) {
      const wf = Array.isArray(t.workflow) ? t.workflow : null
      if (!wf || wf.length === 0) return -1
      const i = wf.findIndex(n => !n.completed_at)
      return i === -1 ? wf.length - 1 : i
    },
    current_node(t) {
      const i = this.current_node_index(t)
      if (i < 0) return null
      return t.workflow[i]
    },
    is_mine(t) {
      return t.creator_id === this.me.id
    },
    is_assigned_to_me(t) {
      const node = this.current_node(t)
      if (!node) return false
      return node.assignee_type === "user" &&
        node.assignee_user_id != null &&
        node.assignee_user_id === this.me.id
    },
    is_assigned_to_my_group(t) {
      const node = this.current_node(t)
      if (!node) return false
      if (node.assignee_type !== "group" || !node.assignee_group) return false
      // Präfix-Match analog zu plugins/permissions.js: ein Nutzer ist Teil der Gruppe,
      // wenn eine seiner pms-Berechtigungen mit der Knoten-Gruppe beginnt
      // (z.B. pms "SFW_ELOCOM_GERAETEWART_rw" passt auf Gruppe "SFW_ELOCOM_GERAETEWART").
      return this.my_groups.some(p => p.startsWith(node.assignee_group))
    },
    assigneeIcon(t) {
      const node = this.current_node(t)
      if (node && node.assignee_type === "group") return "mdi-account-group"
      return "mdi-account"
    },
    assigneeLabel(t) {
      const node = this.current_node(t)
      if (!node) return "kein Workflow"
      if (node.assignee_type === "user") {
        return this.user_name(node.assignee_user_id) || "kein Bearbeiter"
      }
      return node.assignee_group || "kein Bearbeiter"
    },
    currentNodeLabel(t) {
      const wf = Array.isArray(t.workflow) ? t.workflow : null
      if (!wf || wf.length === 0) return t.text || ""
      const i = this.current_node_index(t)
      const node = wf[i]
      if (!node) return t.text || ""
      return `Schritt ${i + 1}/${wf.length}: ${node.name || "(unbenannt)"}`
    },
    user_name(id) {
      if (id == null) return ""
      const u = this.$store.state.ticketsystem.users.find(x => x.id === id)
      return u ? u.displayName : ""
    },
    formatDate(s) {
      if (!s) return ""
      return new Date(s).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    },
    state_config(t) {
      return STATE_CONFIG[t.state] || { name: t.state || "—", color: "grey" }
    },
  },
}
</script>

<style scoped>
.ticket-list {
  max-height: 75vh;
  overflow-y: auto;
}
.ticket-chip-group >>> .v-slide-group__content {
  gap: 4px;
}
</style>
